import type { FormEvent } from 'react';
import { useMemo, useRef, useState } from 'react';
import { ArrowLeftRight, Check, CircleAlert, Copy } from 'lucide-react';
import {
  LanguageCodeSchema,
  type LanguageCode,
  type Register,
  type TranslationRequest,
} from '@zolotalk/shared-types';
import { Button, Select, Textarea } from '../components/ui';
import { translationApi } from '../api/client';
import './TextTranslatePage.css';

const REGISTER_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'formal', label: 'Formal' },
  { value: 'casual', label: 'Casual' },
];

const GENERIC_ERROR = 'We could not translate that text. Please try again.';

function getLanguageDisplayNames(locale: string): Intl.DisplayNames | null {
  for (const candidate of [locale, 'en']) {
    try {
      return new Intl.DisplayNames([candidate], { type: 'language' });
    } catch {
      continue;
    }
  }
  return null;
}

export function TextTranslatePage() {
  const [sourceText, setSourceText] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState<LanguageCode>('en');
  const [targetLanguage, setTargetLanguage] = useState<LanguageCode>('es');
  const [register, setRegister] = useState<Register>('auto');
  const [translation, setTranslation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const sourceInputRef = useRef<HTMLTextAreaElement>(null);

  const languageOptions = useMemo(() => {
    const names = getLanguageDisplayNames(navigator.language);
    return LanguageCodeSchema.options.map(code => ({
      value: code,
      label: names?.of(code) ?? code.toUpperCase(),
    }));
  }, []);

  const resizeSourceInput = () => {
    const input = sourceInputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${input.scrollHeight}px`;
  };

  const swapLanguages = () => {
    setSourceLanguage(targetLanguage);
    setTargetLanguage(sourceLanguage);
    if (translation) {
      setSourceText(translation);
      setTranslation('');
      requestAnimationFrame(resizeSourceInput);
    }
  };

  const handleTranslate = async (event: FormEvent) => {
    event.preventDefault();
    if (!sourceText.trim() || loading) return;

    setLoading(true);
    setError(null);
    setCopied(false);

    try {
      const request: TranslationRequest = {
        text: sourceText,
        sourceLanguage,
        targetLanguage,
        register,
      };
      const data = await translationApi.translate(request);
      setTranslation(data.translatedText);
    } catch {
      setError(GENERIC_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!translation) return;
    await navigator.clipboard.writeText(translation);
    setCopied(true);
  };

  return (
    <div className="text-translate-page">
      <div className="container">
        <header className="translation-page-header">
          <p className="translation-eyebrow">Translate</p>
          <h1>Text Translation</h1>
          <p>Preserve meaning and choose the tone that fits the moment.</p>
        </header>

        <form className="translation-workspace" onSubmit={handleTranslate}>
          <div className="translation-toolbar">
            <Select
              id="source-language"
              label="Source language"
              value={sourceLanguage}
              onChange={event => setSourceLanguage(event.target.value as LanguageCode)}
              options={languageOptions}
            />
            <button
              type="button"
              className="language-swap"
              onClick={swapLanguages}
              aria-label="Swap source and target languages"
            >
              <ArrowLeftRight size={20} aria-hidden="true" />
            </button>
            <Select
              id="target-language"
              label="Target language"
              value={targetLanguage}
              onChange={event => setTargetLanguage(event.target.value as LanguageCode)}
              options={languageOptions}
            />
          </div>

          <div className="translation-register">
            <Select
              id="register"
              label="Register / tone"
              value={register}
              onChange={event => setRegister(event.target.value as Register)}
              options={REGISTER_OPTIONS}
            />
            <p>Auto preserves the tone detected in your text.</p>
          </div>

          <div className="translation-panes">
            <section className="translation-pane source-pane" aria-labelledby="source-pane-title">
              <h2 id="source-pane-title">Original text</h2>
              <Textarea
                ref={sourceInputRef}
                id="source-text"
                aria-label="Text to translate"
                value={sourceText}
                onChange={event => {
                  setSourceText(event.target.value);
                  resizeSourceInput();
                }}
                placeholder="Enter text to translate…"
                rows={7}
                maxLength={5000}
              />
            </section>

            <section className="translation-pane result-pane" aria-labelledby="result-pane-title">
              <div className="translation-pane-heading">
                <h2 id="result-pane-title">Translation</h2>
                {translation && !loading && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    aria-label={copied ? 'Translation copied' : 'Copy translation'}
                  >
                    {copied ? (
                      <Check size={18} aria-hidden="true" />
                    ) : (
                      <Copy size={18} aria-hidden="true" />
                    )}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                )}
              </div>

              {loading ? (
                <div className="translation-skeleton" role="status" aria-label="Translating text">
                  <span />
                  <span />
                  <span />
                </div>
              ) : translation ? (
                <p className="translated-text">{translation}</p>
              ) : (
                <p className="translation-placeholder">Your translation will appear here.</p>
              )}
            </section>
          </div>

          {error && (
            <div className="translation-error" role="alert">
              <CircleAlert size={20} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <div className="translation-actions">
            <Button type="submit" size="lg" loading={loading} disabled={!sourceText.trim()}>
              {loading ? 'Translating…' : error ? 'Try again' : 'Translate'}
            </Button>
          </div>
        </form>

        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {translation && !loading ? 'Translation complete.' : ''}
        </div>
      </div>
    </div>
  );
}
